import React from "react";
import { Area, Player } from "@/lib/types";
import { LoadingSmall } from "@/components/LoadingSmall";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { getArea, getPlayer } from "@/sideEffects/api";
import { selectActiveFirm } from "@/store/appSelectors";
import { useSelector } from "react-redux";

interface AreaNode extends Area {
  children: AreaNode[];
  level: number;
  players: Player[];
}

interface NestedAreaSelectorProps {
  selectedAreaIds: number[];
  onSelectionChange: (selectedIds: number[]) => void;
  disabled?: boolean;
  showPlayers?: boolean;
  selectedPlayerIds?: number[];
}

export const NestedAreaSelector: React.FC<NestedAreaSelectorProps> = ({
  selectedAreaIds,
  onSelectionChange,
  disabled = false,
  showPlayers = false,
  selectedPlayerIds = [],
}) => {
  const activeFirm = useSelector(selectActiveFirm);
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set());

  const {
    data: areaData,
    isLoading: isLoadingAreas,
    isFetchedAfterMount,
  } = useQuery({
    queryKey: ["getArea", activeFirm?.firmGuid],
    queryFn: () => getArea(String(activeFirm?.firmGuid)),
  });

  const { data: playerData, isLoading: isLoadingPlayers } = useQuery({
    queryKey: ["getPlayer", activeFirm?.firmGuid, "nestedAreaSelector"],
    queryFn: () =>
      getPlayer({ numberOfObjectsPerPage: 999999, pageNumber: 1 }, { Firm_Id: String(activeFirm?.firmGuid) }),
    enabled: showPlayers && !!activeFirm?.firmGuid,
  });

  const areas = areaData?.data?.result || [];
  const players = playerData?.data?.result || [];

  // Group players by their areaintId
  const playersByArea = players.reduce((acc: Record<number, Player[]>, player) => {
    const areaId = player.areaintId;
    if (!acc[areaId]) {
      acc[areaId] = [];
    }
    acc[areaId].push(player);
    return acc;
  }, {});

  // Build tree structure from flat array
  const buildTree = (areas: Area[]): AreaNode[] => {
    const areaMap = new Map<number, AreaNode>();
    const rootNodes: AreaNode[] = [];

    // Initialize all nodes
    areas.forEach((area) => {
      areaMap.set(area.intId, {
        ...area,
        children: [],
        level: 0,
        players: playersByArea[area.intId] || [],
      });
    });

    // Build parent-child relationships
    areas.forEach((area) => {
      const node = areaMap.get(area.intId);
      if (!node) return;

      if (area.motherAreaintId && area.motherAreaintId !== 0) {
        const parent = areaMap.get(area.motherAreaintId);
        if (parent) {
          node.level = parent.level + 1;
          parent.children.push(node);
        } else {
          // Parent not found, treat as root
          rootNodes.push(node);
        }
      } else {
        // Root area (no parent or motherAreaintId is 0)
        rootNodes.push(node);
      }
    });

    return rootNodes;
  };

  const areaTree = buildTree(areas);

  // Expand all nodes by default
  useEffect(() => {
    if (!isFetchedAfterMount) return;
    const getAllNodeIds = (nodes: AreaNode[]): number[] => {
      const nodeIds: number[] = [];
      const collectNodeIds = (nodeList: AreaNode[]) => {
        nodeList.forEach((node) => {
          nodeIds.push(node.intId);
          collectNodeIds(node.children);
        });
      };
      collectNodeIds(nodes);
      return nodeIds;
    };

    const allNodeIds = getAllNodeIds(areaTree);
    setExpandedNodes(new Set(allNodeIds));
  }, [areas, isFetchedAfterMount]);

  // Check if a node is selected
  const isSelected = (node: AreaNode): boolean => {
    return selectedAreaIds.includes(node.intId);
  };

  const handleNodeSelect = (node: AreaNode, checked: boolean) => {
    let newSelectedIds = [...selectedAreaIds];

    if (checked) {
      // Add only the clicked node
      newSelectedIds = [...newSelectedIds, node.intId];
    } else {
      // Remove only the clicked node
      newSelectedIds = newSelectedIds.filter((id) => id !== node.intId);
    }

    onSelectionChange(newSelectedIds);
  };

  const toggleExpanded = (nodeId: number) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  const renderPlayer = (player: Player, areaLevel: number): React.ReactNode => {
    const isPlayerSelected = selectedPlayerIds.includes(player.intId) || selectedAreaIds.includes(player.areaintId);
    const indentLevel = (areaLevel + 1) * 20;

    return (
      <div key={`player-${player.intId}`} className="w-full">
        <div
          className="flex items-center py-1 hover:bg-muted/50 rounded-sm"
          style={{ paddingLeft: `${indentLevel}px` }}
        >
          <div className="w-4 h-4 mr-2" />
          <div className="flex items-center space-x-2 flex-1">
            <Checkbox
              id={`player-${player.intId}`}
              checked={isPlayerSelected}
              disabled={true}
              className="pointer-events-none"
            />
            <Label htmlFor={`player-${player.intId}`} className="text-sm cursor-pointer flex-1 text-muted-foreground">
              {player.name}
            </Label>
          </div>
        </div>
      </div>
    );
  };

  const renderNode = (node: AreaNode): React.ReactNode => {
    const hasChildren = node.children.length > 0;
    const hasPlayers = showPlayers && node.players.length > 0;
    const hasChildrenOrPlayers = hasChildren || hasPlayers;
    const isExpanded = expandedNodes.has(node.intId);
    const nodeSelected = isSelected(node);
    const indentLevel = node.level * 20;

    return (
      <div key={node.intId} className="w-full">
        <div
          className={`flex items-center py-1 hover:bg-muted/10 rounded-sm`}
          style={{ paddingLeft: `${indentLevel}px` }}
        >
          {hasChildrenOrPlayers ? (
            <button
              type="button"
              onClick={() => toggleExpanded(node.intId)}
              className="flex items-center justify-center w-4 h-4 mr-2 hover:bg-muted rounded-sm"
              disabled={disabled}
            >
              {isExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            </button>
          ) : (
            <div className="w-4 h-4 mr-2" />
          )}

          <div className="flex items-center space-x-2 flex-1">
            <Checkbox
              id={`area-${node.intId}`}
              checked={nodeSelected}
              onCheckedChange={(checked) => handleNodeSelect(node, checked as boolean)}
              disabled={disabled}
            />
            <Label
              htmlFor={`area-${node.intId}`}
              className={`text-sm cursor-pointer flex-1 ${disabled ? "" : ""}`}
            >
              {node.name}
            </Label>
          </div>
        </div>

        {hasChildrenOrPlayers && isExpanded && (
          <div className="ml-4">
            {node.children.map(renderNode)}
            {showPlayers && node.players.map((player) => renderPlayer(player, node.level))}
          </div>
        )}
      </div>
    );
  };

  const isLoading = isLoadingAreas || (showPlayers && isLoadingPlayers);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <LoadingSmall />
      </div>
    );
  }

  if (areas.length === 0) {
    return <div className="text-center text-muted-foreground py-4">No areas available</div>;
  }

  return <div className="space-y-1 max-h-96 overflow-y-auto">{areaTree.map(renderNode)}</div>;
};
